import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, Loader2, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { HEAD } from "@/components/PdPill";

const SAMPLE = "PREET INDANE\t7500000080309281\tRAVI KUMAR\t9864804665\nSHIV SHAKTI HPCL\t7009876543210\tSITA DEVI\nPREET INDANE\t4455 6677 8899";

const TypeTag = ({ t, dup }) => (
  <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${dup ? "bg-[#C8553D]/15 text-[#C8553D]" : t === "aadhaar" ? "bg-[#8A6046]/15 text-[#8A6046]" : "bg-[#385C4B]/12 text-[#385C4B]"}`}>
    {dup ? "Duplicate" : t === "aadhaar" ? "Aadhaar" : "LPG ID"}
  </span>
);

export default function EkycBulkPaste() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [skipped, setSkipped] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setSkipped(new Set());
    try {
      const { data } = await api.post("/agency-ekyc/bulk/preview", { text });
      setPreview(data);
      if (!data.groups.length && !data.unmatched.length) toast.info("Koi valid ID nahi mili");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (gid) => setSkipped((s) => {
    const n = new Set(s);
    n.has(gid) ? n.delete(gid) : n.add(gid);
    return n;
  });

  const totals = useMemo(() => {
    if (!preview) return { fresh: 0, groups: 0 };
    let fresh = 0, groups = 0;
    for (const g of preview.groups) {
      if (skipped.has(g.group_id)) continue;
      fresh += g.fresh;
      if (g.fresh) groups += 1;
    }
    return { fresh, groups };
  }, [preview, skipped]);

  const dispatch = async () => {
    const groups = preview.groups
      .filter((g) => !skipped.has(g.group_id))
      .map((g) => ({ group_id: g.group_id, rows: g.rows.filter((r) => !r.dup) }))
      .filter((g) => g.rows.length);
    if (!groups.length) { toast.info("Bhejne ke liye kuch nahi"); return; }
    setCommitting(true);
    try {
      const { data } = await api.post("/agency-ekyc/bulk/commit", { groups });
      toast.success(`${data.inserted} EKYC entries bheji · ${data.skipped_duplicates} duplicate skip`);
      setText("");
      setPreview(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCommitting(false);
    }
  };

  const hidden = preview?.groups.filter((g) => skipped.has(g.group_id)) || [];

  return (
    <div className="min-h-screen bg-[#F4F3EC]" data-testid="ekyc-paste-page">
      <header className="bg-[#385C4B] text-white sticky top-0 z-30 shadow-[0_6px_20px_rgba(56,92,75,0.2)]">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-2.5">
          <button onClick={() => navigate("/ekyc")} className="grid place-items-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors" data-testid="ekyc-paste-back" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className={`${HEAD} text-lg sm:text-xl font-semibold`}>EKYC · Bulk Paste</h1>
          <span className="ml-auto text-[10px] font-mono uppercase bg-white/15 px-2 py-0.5 rounded-full">Admin</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-5 space-y-4">
        <section className="bg-white border border-[#E0DFD6] rounded-xl p-4 space-y-3">
          <p className="text-xs text-[#6B6B61]">
            Agency ka naam + LPG ID / Aadhaar (+ naam, mobile) paste karo. Har row apni agency ke EKYC tab me chali jayegi.
            <b className="text-[#385C4B]"> LPG ID hamesha unique</b> — dobara kabhi nahi jayegi. Aadhaar repeat ho sakta hai.
          </p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} data-testid="ekyc-paste-input"
            placeholder={SAMPLE}
            className="w-full rounded-lg border border-[#E0DFD6] bg-[#FAFAF6] p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30" />
          <button onClick={runPreview} disabled={loading || !text.trim()} data-testid="ekyc-paste-preview"
            className="h-10 px-5 rounded-full bg-[#385C4B] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 transition-transform active:scale-95">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview
          </button>
        </section>

        {preview && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono tracking-[0.18em] uppercase text-[#6B6B61]" data-testid="ekyc-preview-header">
              Matched Agencies · {hidden.length ? `${preview.groups.length - hidden.length} of ${preview.groups.length}` : preview.groups.length}
              {hidden.map((g) => (
                <button key={g.group_id} onClick={() => toggle(g.group_id)} data-testid={`ekyc-restore-${g.group_id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#8A6046]/40 bg-[#8A6046]/10 text-[#8A6046] normal-case tracking-normal hover:bg-[#8A6046]/20">
                  {g.group_name}<RotateCcw className="w-3 h-3" />
                </button>
              ))}
            </div>

            {preview.groups.filter((g) => !skipped.has(g.group_id)).map((g) => (
              <section key={g.group_id} className="bg-white border border-[#E0DFD6] rounded-xl overflow-hidden" data-testid={`ekyc-group-card-${g.group_id}`}>
                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#EFEEE6] border-b border-[#E0DFD6]">
                  <span className="font-semibold text-[#1C1C19] text-sm flex-1 truncate">{g.group_name}</span>
                  <span className="text-xs text-[#385C4B] tabular-nums">{g.fresh} fresh</span>
                  {g.dup > 0 && <span className="text-xs text-[#C8553D] tabular-nums">· {g.dup} dup</span>}
                  <label className="flex items-center gap-1.5 cursor-pointer text-[10px] font-mono uppercase tracking-wider text-[#385C4B] ml-2">
                    <input type="checkbox" checked onChange={() => toggle(g.group_id)} data-testid={`ekyc-select-${g.group_id}`} className="w-4 h-4 accent-[#385C4B]" />
                    Send
                  </label>
                </div>
                <div className="max-h-56 overflow-y-auto divide-y divide-[#F6F5EF]">
                  {g.rows.map((r, i) => (
                    <div key={i} className={`flex items-center gap-2 px-4 py-1.5 text-sm ${r.dup ? "opacity-50" : ""}`}>
                      <span className="font-mono font-semibold text-[#1C1C19]">{r.ident}</span>
                      <TypeTag t={r.ident_type} dup={r.dup} />
                      <span className="text-xs text-[#6B6B61] truncate">{r.name}{r.name && r.mobile ? " · " : ""}{r.mobile}</span>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {preview.unmatched.length > 0 && (
              <section className="bg-white border border-[#C8553D]/30 rounded-xl overflow-hidden" data-testid="ekyc-unmatched">
                <div className="px-4 py-2.5 bg-[#C8553D]/10 border-b border-[#C8553D]/20 text-xs font-semibold text-[#C8553D]">
                  Unmatched · {preview.unmatched.length} (inki agency match nahi hui — ye nahi jaayengi)
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-[#F6F5EF]">
                  {preview.unmatched.map((u, i) => (
                    <div key={i} className="flex items-center gap-2 px-4 py-1.5 text-sm">
                      <span className="font-mono font-semibold">{u.ident}</span>
                      <TypeTag t={u.ident_type} />
                      <span className="text-xs text-[#6B6B61] truncate">{u.agency_label}</span>
                      {u.suggestion && <span className="ml-auto text-[10px] text-[#8A6046]">Did you mean: {u.suggestion}?</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="sticky bottom-3">
              <button onClick={dispatch} disabled={committing || !totals.fresh} data-testid="ekyc-dispatch"
                className="w-full h-12 rounded-full bg-[#385C4B] text-white font-semibold inline-flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 transition-transform active:scale-[0.99]">
                {committing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                Dispatch {totals.fresh} EKYC to {totals.groups} {totals.groups === 1 ? "agency" : "agencies"}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
